if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.notificationConfirmed = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        const form = this.querySelector('form');
        if (form) {
          const formId = form.getAttribute('id');
          const bundleCartComponent = document.querySelector(
            `product-bundle-cart-component[data-product-form-id="${formId}"][data-active="true"]`
          );
          if (bundleCartComponent) {
            return;
          }

          const addonsComponent = document.querySelector(
            `product-addons-component[data-product-form-id="${formId}"]`
          );
          if (addonsComponent) {
            return;
          }
        }

        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const modal = document.querySelector('product-notification-modal');
        if (modal && !this.notificationConfirmed) {
          const productId = this.dataset.productId;
          if (productId) {
            try {
              if (sessionStorage.getItem('product-notification-dismissed-' + productId)) {
                this.notificationConfirmed = true;
                this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                return;
              }
            } catch (e) {}
          }
          modal.show(this);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }

            this.notificationConfirmed = false;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/**
 * Shared multi-line cart add (used by product-addons and product-bundle-cart).
 */
async function addLineItemsToCart(items, form, options = {}) {
  const {
    source = 'product-addons',
    productVariantId = items[0]?.id,
    resetAddonCheckboxes = false,
    addonCheckboxes = null,
  } = options;

  const addToCartButton = form.querySelector('button[type="submit"]');
  // Target the text label, not a Material Symbols icon span — list/grid buttons
  // put the icon span first, so a plain querySelector('span') would write the
  // success text into the icon font and blow up the button.
  const buttonText = addToCartButton?.querySelector('span:not([class*="material-symbols"])');
  const spinner = addToCartButton?.querySelector('.loading__spinner');

  if (addToCartButton) {
    addToCartButton.setAttribute('aria-disabled', 'true');
    addToCartButton.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');
  }

  try {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const requestBody = { items };

    if (cart) {
      const sections = cart.getSectionsToRender().map((section) => section.id);
      requestBody.sections = sections.join(',');
      requestBody.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || errorData.message || window.cartStrings?.addError);
    }

    const data = await response.json();

    const startMarker =
      typeof CartPerformance !== 'undefined' ? CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId,
        cartData: data,
      });
      if (startMarker && typeof CartPerformance !== 'undefined') {
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
      }
    }

    if (cart) {
      if (typeof CartPerformance !== 'undefined') {
        CartPerformance.measure('add:paint-updated-sections', () => {
          cart.renderContents(data);
        });
      } else {
        cart.renderContents(data);
      }

      if (cart.classList && cart.classList.contains('is-empty') && data.items && data.items.length > 0) {
        cart.classList.remove('is-empty');
      }
    }

    if (buttonText) {
      const originalText = buttonText.textContent;
      buttonText.textContent = `✓ ${window.cartStrings?.addedToCart}`;
      setTimeout(() => {
        buttonText.textContent = originalText;
      }, 2000);
    }

    if (resetAddonCheckboxes && addonCheckboxes) {
      addonCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }

    return data;
  } catch (error) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartError, {
        source,
        errors: error.message,
        message: error.message,
      });
    }

    if (buttonText) {
      const originalText = buttonText.textContent;
      buttonText.textContent = error.message || window.cartStrings?.addError;
      setTimeout(() => {
        buttonText.textContent = originalText;
      }, 3000);
    }

    throw error;
  } finally {
    if (addToCartButton) {
      addToCartButton.removeAttribute('aria-disabled');
      addToCartButton.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }
}

function extractLineItemPropertiesFromForm(formData) {
  const properties = {};
  for (const [key, value] of formData.entries()) {
    if (key.startsWith('properties[')) {
      const propKey = key.replace('properties[', '').replace(']', '');
      properties[propKey] = value;
    }
  }
  return properties;
}

function collectAddonLineItems(form, bundleParentQuantity) {
  const formId = form.getAttribute('id');
  const addonsComponent = document.querySelector(
    `product-addons-component[data-product-form-id="${formId}"]`
  );
  if (!addonsComponent) return [];

  const checkboxes = addonsComponent.querySelectorAll('.product-addon-item__checkbox');
  const quantity = bundleParentQuantity;

  return Array.from(checkboxes)
    .filter((checkbox) => checkbox.checked && !checkbox.disabled)
    .map((checkbox) => ({
      id: checkbox.dataset.addonId,
      quantity,
    }));
}

/* Product bundle cart — multi-line add for bundle configurator PDP */
if (!customElements.get('product-bundle-cart-component')) {
  customElements.define(
    'product-bundle-cart-component',
    class ProductBundleCartComponent extends HTMLElement {
      constructor() {
        super();
        this.productFormId = this.dataset.productFormId;
        this.isActive = this.dataset.active === 'true';
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }

      connectedCallback() {
        if (this.isActive) this.init();
      }

      disconnectedCallback() {
        const productForm = document.getElementById(this.productFormId);
        if (productForm) {
          productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
        }
      }

      init() {
        if (this._initialized) return;

        const productForm = document.getElementById(this.productFormId);
        if (!productForm) return;

        const configurator = this.getConfigurator();
        if (!configurator) return;

        this._initialized = true;
        productForm.addEventListener('submit', this.boundHandleFormSubmit, { capture: true });
      }

      setActive(active) {
        this.isActive = active;
        this.dataset.active = String(active);
        if (active) {
          this.init();
          return;
        }

        const productForm = document.getElementById(this.productFormId);
        if (productForm && this._initialized) {
          productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
        }
        this._initialized = false;
      }

      getConfigurator() {
        const scoped = document.querySelector(
          `[data-bundle-configurator][data-product-form-id="${this.productFormId}"]`
        );
        return scoped || this.closest('[data-bundle-configurator]');
      }

      buildCartItems(form) {
        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        const parentQuantity = parseInt(formData.get('quantity') || '1', 10);

        if (!mainVariantId) {
          throw new Error(window.cartStrings?.bundleSelectOption);
        }
        if (!Number.isInteger(parentQuantity) || parentQuantity <= 0) {
          throw new Error('Bundle quantity must be greater than zero.');
        }

        const configurator = this.getConfigurator();
        if (!configurator || configurator.dataset.configPresent !== 'true') {
          throw new Error('This bundle configuration could not be loaded.');
        }
        const rows = configurator.querySelectorAll('[data-bundle-configurator-item]');
        const expectedComponentCount = parseInt(configurator.dataset.expectedComponentCount || '0', 10);
        if (
          !Number.isInteger(expectedComponentCount) ||
          expectedComponentCount <= 0 ||
          rows.length !== expectedComponentCount
        ) {
          throw new Error('This bundle configuration is incomplete.');
        }

        const components = [];
        rows.forEach((row, idx) => {
          const variantId = row.dataset.variantId;
          if (!variantId) {
            throw new Error(window.cartStrings?.bundleVariantRequired);
          }
          if (row.dataset.available === 'false') {
            throw new Error('One or more bundle components are unavailable.');
          }

          const select = row.querySelector('[data-bundle-variant-select]');
          if (select) {
            const selectedOption = select.selectedOptions[0];
            if (selectedOption?.disabled) {
              throw new Error(window.cartStrings?.bundleComponentUnavailable);
            }
          }

          const componentIndex = parseInt(row.dataset.componentIndex || String(idx), 10);
          const rowQuantity = parseInt(row.dataset.quantity || '1', 10);
          const parsedVariantId = parseInt(variantId, 10);
          if (!Number.isInteger(componentIndex) || !Number.isInteger(rowQuantity) || rowQuantity <= 0) {
            throw new Error('This bundle has an invalid component configuration.');
          }
          if (!Number.isInteger(parsedVariantId) || parsedVariantId <= 0) {
            throw new Error('This bundle has an invalid component variant.');
          }

          components.push({
            i: componentIndex,
            v: parsedVariantId,
            q: rowQuantity,
          });
        });

        const formProperties = extractLineItemPropertiesFromForm(formData);
        if (configurator.dataset.pricingMode === 'dynamic') {
          const groupId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `bundle-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const parentTitle = configurator.dataset.bundleParentTitle || '';
          const componentCount = String(components.length);

          const items = components.map((component) => ({
            id: component.v,
            quantity: component.q * parentQuantity,
            properties: {
              ...formProperties,
              _bundle_group_id: groupId,
              _bundle_parent_variant_id: String(mainVariantId),
              _bundle_parent_title: parentTitle,
              _bundle_component_index: String(component.i),
              _bundle_component_count: componentCount,
              _bundle_component_quantity: String(component.q),
            },
          }));

          collectAddonLineItems(form, parentQuantity).forEach((addonItem) => {
            items.push(addonItem);
          });

          return { items, mainVariantId };
        }

        const parentProperties = {
          ...formProperties,
          // Read by the bundle-expand cart transform to expand the bundle into
          // its components. _bundle_handle/_bundle_type were unused downstream
          // and only cluttered the order, so they're intentionally omitted.
          _bundle_components: JSON.stringify(components),
        };

        const items = [
          {
            id: mainVariantId,
            quantity: parentQuantity,
            properties: parentProperties,
          },
        ];

        const addonItems = collectAddonLineItems(form, parentQuantity);
        addonItems.forEach((addonItem) => {
          items.push(addonItem);
        });

        return { items, mainVariantId };
      }

      handleFormSubmit(event) {
        if (!this.isActive) return;

        const form = event.target;
        const formId = form.getAttribute('id');
        if (formId !== this.productFormId) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        let items;
        let mainVariantId;

        try {
          const built = this.buildCartItems(form);
          items = built.items;
          mainVariantId = built.mainVariantId;
        } catch (error) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-bundle-cart',
              errors: error.message,
              message: error.message,
            });
          }
          const buttonText = form.querySelector('button[type="submit"] span');
          if (buttonText) {
            buttonText.textContent = error.message;
            setTimeout(() => {
              buttonText.textContent = window.variantStrings?.addToCart;
            }, 3000);
          }
          return;
        }

        const addonsComponent = document.querySelector(
          `product-addons-component[data-product-form-id="${formId}"]`
        );
        const addonCheckboxes = addonsComponent?.querySelectorAll('.product-addon-item__checkbox');

        addLineItemsToCart(items, form, {
          source: 'product-bundle-cart',
          productVariantId: mainVariantId,
          resetAddonCheckboxes: Boolean(addonCheckboxes?.length),
          addonCheckboxes,
        }).catch(() => {});
      }
    }
  );
}

/* Product Addons Component */
if (!customElements.get('product-addons-component')) {
  customElements.define(
    'product-addons-component',
    class ProductAddonsComponent extends HTMLElement {
      constructor() {
        super();
        this.productFormId = this.dataset.productFormId;
        this.checkboxes = this.querySelectorAll('.product-addon-item__checkbox');
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        const productForm = document.getElementById(this.productFormId);
        if (productForm) {
          productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
        }
      }

      init() {
        if (this._initialized) return;

        const productForm = document.getElementById(this.productFormId);
        if (!productForm) return;

        const bundleCartComponent = document.querySelector(
          `product-bundle-cart-component[data-product-form-id="${this.productFormId}"][data-active="true"]`
        );
        if (bundleCartComponent) {
          return;
        }

        this._initialized = true;
        productForm.addEventListener('submit', this.boundHandleFormSubmit, { capture: true });
      }

      handleFormSubmit(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const form = event.target;
        const formData = new FormData(form);

        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);

        if (!mainVariantId) return;

        const items = [
          {
            id: mainVariantId,
            quantity: quantity,
          },
        ];

        const properties = extractLineItemPropertiesFromForm(formData);
        if (Object.keys(properties).length > 0) {
          items[0].properties = properties;
        }

        const selectedAddons = Array.from(this.checkboxes).filter(
          (checkbox) => checkbox.checked && !checkbox.disabled
        );

        selectedAddons.forEach((checkbox) => {
          items.push({
            id: checkbox.dataset.addonId,
            quantity: quantity,
          });
        });

        addLineItemsToCart(items, form, {
          source: 'product-addons',
          productVariantId: mainVariantId,
          resetAddonCheckboxes: true,
          addonCheckboxes: this.checkboxes,
        }).catch(() => {});
      }
    }
  );
}
